import { red } from '@mui/material/colors';
import { createTheme } from '@mui/material/styles';

import { ParagonColors } from '@paragon/client-tools';

const error = red;

declare module '@mui/material/styles' {
  interface Palette {
    selectedList?: {
      main: string;
    };
    canvas: {
      main: string;
    };
    backContrast: {
      main: string;
      light: string;
      dark: string;
    };
    paragon: {
      main: string;
    };
    drawerDialog: {
      main: string;
    };
    border: {
      main: string;
    };
    appBar: {
      main: string;
      text: string;
      progress: string;
    };
  }

  interface PaletteOptions {
    selectedList?: {
      main: string;
    };
    canvas?: {
      main: string;
    };
    backContrast?: {
      main: string;
      light: string;
      dark: string;
    };
    paragon?: {
      main: string;
    };
    drawerDialog?: {
      main: string;
    };
    border?: {
      main: string;
    };
    appBar?: {
      main: string;
      text: string;
      progress: string;
    };
  }
}

export const darkTheme = createTheme({
  typography: {
    fontFamily: 'Roboto Condensed, Figtree, sans-serif',
    // Figtree Semi-Bold for the App Header/Pargon name
    h1: {
      fontSize: '1.2rem',
      fontFamily: 'Figtree',
      fontWeight: 600,
      webkitFontSmoothing: 'antialiased',
    },
    // Figtree Regular for Primary Headings
    h2: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h3: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h4: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h5: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h6: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Regular for Standard Headings and Actions
    subtitle1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    subtitle2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    button: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    overline: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Light for captions
    caption: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 300,
      webkitFontSmoothing: 'antialiased',
    },
  },
  components: {
    // MUI currently applies a semi-transparent gradient over dialogs causing the specified theme colors to be darker or lighter than intended.
    // This code removes that gradient, which they apply as a backgroundImage, so the intended theme colors are properly displayed.
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: ParagonColors.paragold,
          },
        },
      },
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: ParagonColors.blue,
    },
    secondary: {
      main: ParagonColors.paragold,
    },
    error: {
      main: error[400],
    },
    paragon: {
      main: ParagonColors.paragold,
    },
    canvas: {
      main: ParagonColors.canvasDark,
    },
    selectedList: {
      main: '#757575',
    },
    background: {
      default: ParagonColors.background,
      paper: ParagonColors.background,
    },
    backContrast: {
      main: '#2d2d2d',
      light: '#3A3A3A',
      dark: '#222222',
    },
    drawerDialog: {
      main: '#222222',
    },
    border: {
      main: '#c3c3c3',
    },
    appBar: {
      main: '#424242',
      text: '#fff',
      progress: ParagonColors.paragold,
    },
  },
});

export const lightTheme = createTheme({
  typography: {
    fontFamily: 'Roboto Condensed, Figtree, sans-serif',
    // Figtree Semi-Bold for the App Header/Pargon name
    h1: {
      fontSize: '1.2rem',
      fontFamily: 'Figtree',
      fontWeight: 600,
      webkitFontSmoothing: 'antialiased',
    },
    // Figtree Regular for Primary Headings
    h2: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h3: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h4: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h5: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h6: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Regular for Standard Headings and Actions
    subtitle1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    subtitle2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    button: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    overline: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Light for captions
    caption: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 300,
      webkitFontSmoothing: 'antialiased',
    },
  },
  components: {
    // MUI currently applies a semi-transparent gradient over dialogs causing the specified theme colors to be darker or lighter than intended.
    // This code removes that gradient, which they apply as a backgroundImage, so the intended theme colors are properly displayed.
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            backgroundColor: 'transparent',
            borderColor: `#333`,
            color: `#333`,
            ':hover': {
              color: '#333',
            },
          },
        },
      ],
    },
  },
  palette: {
    primary: {
      main: ParagonColors.blue,
    },
    secondary: {
      main: ParagonColors.paragold,
    },
    error: {
      main: error[500],
    },
    paragon: {
      main: ParagonColors.paragold,
    },
    canvas: {
      main: ParagonColors.canvasLight,
    },
    selectedList: {
      main: 'rgba(77, 147, 120, 0.6)',
    },
    background: {
      default: '#EEEEEE',
      paper: '#EEEEEE',
    },
    backContrast: {
      main: '#eee',
      light: '#fff',
      dark: '#ccc',
    },
    drawerDialog: {
      main: '#ccc',
    },
    border: {
      main: '#c3c3c3',
    },
    appBar: {
      main: '#505050',
      text: '#fff',
      progress: ParagonColors.paragold,
    },
  },
});
